import * as React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { debounce } from 'lodash';
import { getFieldConfig, searchAssets } from 'rest/assets';
import type { State as AppState } from 'state';
import { getAccountId } from 'state/selectors/user';
import type { AssetsPickerOption, AssetsPickerProps } from './assets-picker-dumb';
import { AssetsPickerDumb } from './assets-picker-dumb';
import type { Asset, AssetFieldConfig, AssetPage } from './types';

interface Props extends AssetsPickerProps {
    fieldConfigId: string;
    accountId: string | undefined;
    portalId: number;
}

type FinalProps = Omit<Props, 'loadOptions' | 'doInitialSearch'>;

interface State {
    fieldConfig: AssetFieldConfig;
    isFieldConfigLoaded: boolean;
}

export class AssetsPicker extends React.PureComponent<FinalProps, State> {
    static readonly searchAssetDebounceWaitTime: number = 400;

    state: State = {
        fieldConfig: {
            types: [],
        },
        isFieldConfigLoaded: false,
    };

    loadOptions = (inputValue: string, callback: (options: AssetsPickerOption[]) => void) => {
        // Disabling existing violations, should be fixed when revisited.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.searchAssetsDebounced(inputValue, callback);
    };

    componentDidMount() {
        return getFieldConfig(this.props.fieldConfigId, this.props.portalId)
            .toPromise()
            .then((fieldConfig: AssetFieldConfig) => this.setState({ fieldConfig, isFieldConfigLoaded: true }))
            .catch((_) => {
                // TODO: Consider re-visiting this error handling. If the field config exists and fails for some
                // unknown reason, the search will potentially fetch incorrectly typed assets.
                this.setState({ isFieldConfigLoaded: true });
            });
    }

    fetchAssets = (inputValue: string, callback: (options: AssetsPickerOption[]) => void) => {
        if (!this.state.isFieldConfigLoaded) {
            // We can't fetch assets until the field config is loaded.
            return Promise.resolve([]);
        }

        const { accountId } = this.props;
        if (!accountId) {
            return Promise.resolve();
        }

        return searchAssets(accountId, inputValue, this.state.fieldConfig)
            .map((response: AssetPage) =>
                response.values.map((asset: Asset) => ({
                    label: asset.label.value,
                    value: `${asset.origin.appKey}/${asset.origin.originId}`,
                    origin: asset.origin,
                }))
            )
            .toPromise()
            .then(callback);
    };

    searchAssetsDebounced = debounce(this.fetchAssets, AssetsPicker.searchAssetDebounceWaitTime);

    render() {
        return (
            <AssetsPickerDumb
                {...this.props}
                loadOptions={this.loadOptions}
                isFieldConfigLoaded={this.state.isFieldConfigLoaded}
                doInitialSearch
            />
        );
    }
}

const selector = createSelector(
    (state: AppState, props: FinalProps) => props.accountId || getAccountId(state),
    (accountId) => ({ accountId })
);

export default connect(selector)(AssetsPicker);
